import React, { useCallback, useEffect, useState } from "react";
import { notification, Row, Col } from "antd";
import "./manageinvitation.css";
import InvitationListTableColumns from "./columns/InvitationListTableColumns";
import SearchInput from "../../generic/elements/search/SearchInput";
import {
  hasMoreInvitationDataState,
  manageInvitationDataState,
  manageInvitationPageState,
} from "../../../components/recoil/manageinvitationsrecoil";
import { useRecoilState, useRecoilValue } from "recoil";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { executeIntent } from "../../utils/IntentExecutor";
import IntentType from "../../../intentexecutor/utils/IntentType";
import TableList from "../../generic/elements/tablelist/TableList";
import BackButton from "../../generic/elements/backbutton/BackButton";
import AddEditWorkforceModal, {
  workforceDataUpdateResponse,
} from "../workforce/AddEditWorkforceModal";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";
import { teamList } from "../../../components/recoil/teamsRecoil";
import { useObservable } from "../../generic/hooks/useObservable";
import ErrorNotificationComponent from "../../../components/generic/error/ErrorNotificationComponent";
import SearchAndSelect from "../../../components/generic/search/SearchAndSelect";
import { EventType } from "../../../service/network/utils/EventType";
import { strings } from "../../utils/strings";
import { statusCode } from "../../utils/statuscodes";
import { convertDateToEODTimestamp } from "../../../utils/dateutils";

const EMAIL_REGEX = /^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/;

const STATUS_FILTER_OPTIONS = [
  { label: "Onboarded", value: "Onboarded" },
  { label: "Invited", value: "Invited" },
];
const ROLE_FILTER_OPTIONS = [
  { label: "Volunteer Coordinator", value: "Volunteer Coordinator" },
  { label: "Volunteer", value: "Volunteer" },
];

const ManageInvitationComponent = () => {
  const [openAddWorkforceModal, setOpenAddWorkforceModal] = useState(false);
  const [showAddVolunteerButton, setShowAddVolunteerButton] = useState(false);
  const configuration = useRecoilValue(currentConfiguration);

  const [tableData, setTableData] = useRecoilState(manageInvitationDataState);
  const [hasMoreData, setHasMoreData] = useRecoilState(
    hasMoreInvitationDataState,
  );
  const teams = useRecoilValue(teamList);
  const [page, setPage] = useRecoilState(manageInvitationPageState);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableLoading, setTableLoading] = useState(false);
  const userJson = useReadMemory(MemoryType.USER);
  const [reload, setReload] = useState(false);
  const [loadingRow, setLoadingRow] = useState({});
  const [editingRowId, setEditingRowId] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const [editEmailError, setEditEmailError] = useState(null);
  const [workforceStatus, setWorkforceStatus] = useState([]);
  const [workforceRole, setWorkforceRole] = useState([]);

  useObservable(workforceDataUpdateResponse, setReload);

  const handleAddWorkforceClick = () => {
    setOpenAddWorkforceModal(!openAddWorkforceModal);
  };

  const resendInvite = useCallback(
    async (item) => {
      const id = item?.userIdentifier;
      if (!id) {
        notification.error({
          message: "Failed to Send Invitation",
          placement: "bottomRight",
        });
        return;
      }

      setLoadingRow((prev) => ({ ...prev, [id]: true }));

      const sendInvitePayload = {
        data: {
          userIdentifier: item.userIdentifier,
          creatorIdentifier: userJson?.userIdentifier,
        },
      };

      try {
        const response = await executeIntent(
          IntentType.SEND_INVITE_TO_WORKFORCE,
          sendInvitePayload,
        );

        if (!response?.status) {
          notification.error({
            message: "Failed to Send Invitation",
            placement: "bottomRight",
          });
          return;
        }

        if (item?.status !== "Invited") {
          const updateWorkforcePayload = {
            data: {
              ...item,
              userIdentifier: item.userIdentifier,
              organizationIdentifier: userJson?.organizationIdentifier,
              status: "Invited",
              creatorIdentifier: userJson?.userIdentifier,
              birthDate: item?.birthDate && convertDateToEODTimestamp(item?.birthDate),
              anniversary: item?.anniversary && convertDateToEODTimestamp(item?.anniversary)
            },
          };
          const updateResponse = await executeIntent(
            IntentType.UPDATE_WORKFORCE,
            updateWorkforcePayload,
          );

          if (!updateResponse?.identifier) {
            notification.error({
              message:
                "Invitation sent, but failed to update invitation status",
              placement: "bottomRight",
            });
            return;
          }
        }

        notification.success({
          message: "Invitation Sent Successfully",
          placement: "bottomRight",
        });
        workforceDataUpdateResponse.next({
          type: EventType.WORKFORCE_DATA_UPDATED,
        });
        setReload((prev) => !prev);
      } catch (err) {
        ErrorNotificationComponent(err);
      } finally {
        setLoadingRow((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [userJson?.userIdentifier, userJson?.organizationIdentifier],
  );

  const cancelEditRow = useCallback(() => {
    setEditingRowId(null);
    setEditEmail("");
    setEditEmailError(null);
  }, []);

  const editRow = useCallback((item) => {
    const id = item?.userIdentifier;
    if (!id) {
      return;
    }
    setEditingRowId(id);
    setEditEmail(item?.email || "");
    setEditEmailError(null);
  }, []);

  const handleEditEmailChange = useCallback((value) => {
    setEditEmail(value);
    setEditEmailError(null);
  }, []);

  const saveEditRow = useCallback(
    async (item) => {
      const id = item?.userIdentifier;
      const trimmedEmail = editEmail?.trim();

      if (!trimmedEmail) {
        setEditEmailError(strings.EMAIL_ERROR_MESSAGE);
        return;
      }
      if (!EMAIL_REGEX.test(trimmedEmail)) {
        setEditEmailError(strings.VALID_EMAIL_ERROR_MESSAGE);
        return;
      }
      if (trimmedEmail === item?.email) {
        cancelEditRow();
        return;
      }

      setLoadingRow((prev) => ({ ...prev, [id]: true }));
      const payload = {
        data: {
          ...item,
          userIdentifier: id,
          email: trimmedEmail,
          organizationIdentifier: userJson?.organizationIdentifier,
          creatorIdentifier: userJson?.userIdentifier,
          status: "Pending Invite",
          replaceInvitationCode: true,
          birthDate: item?.birthDate && convertDateToEODTimestamp(item?.birthDate),
          anniversary: item?.anniversary && convertDateToEODTimestamp(item?.anniversary)
        },
      };

      try {
        const response = await executeIntent(
          IntentType.UPDATE_WORKFORCE,
          payload,
        );
        if (response?.code === statusCode.EMAIL_ALREADY_EXISTS) {
          setEditEmailError(strings.EMAIL_ALREADY_EXISTS);
        } else if (response?.identifier) {
          notification.success({
            message: strings.UPDATE_WORKFORCE_SUCCESS,
            placement: "bottomRight",
          });
          cancelEditRow();
          workforceDataUpdateResponse.next({
            type: EventType.WORKFORCE_DATA_UPDATED,
          });
        } else {
          notification.error({
            message: strings.UPDATE_WORKFORCE_ERROR,
            placement: "bottomRight",
          });
        }
      } catch (err) {
        ErrorNotificationComponent(err);
      } finally {
        setLoadingRow((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [
      editEmail,
      userJson?.userIdentifier,
      userJson?.organizationIdentifier,
      cancelEditRow,
    ],
  );

  const revokeInvite = useCallback(
    async (item) => {
      const id = item?.userIdentifier;
      if (!id) {
        notification.error({
          message: "Failed to Revoke Invitation",
          placement: "bottomRight",
        });
        return;
      }

      setLoadingRow((prev) => ({ ...prev, [id]: true }));
      const payload = {
        data: {
          ...item,
          userIdentifier: item.userIdentifier,
          organizationIdentifier: userJson?.organizationIdentifier,
          status: "Revoked",
          creatorIdentifier: userJson?.userIdentifier,
          birthDate: item?.birthDate && convertDateToEODTimestamp(item?.birthDate),
          anniversary: item?.anniversary && convertDateToEODTimestamp(item?.anniversary)
        },
      };

      try {
        const response = await executeIntent(
          IntentType.UPDATE_WORKFORCE,
          payload,
        );
        if (response?.identifier) {
          notification.success({
            message: "Invitation Revoked Successfully",
            placement: "bottomRight",
          });
          workforceDataUpdateResponse.next({
            type: EventType.WORKFORCE_DATA_UPDATED,
          });
        } else {
          notification.error({
            message: "Failed to Revoke Invitation",
            placement: "bottomRight",
          });
        }
      } catch (err) {
        ErrorNotificationComponent(err);
      } finally {
        setLoadingRow((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [userJson?.userIdentifier, userJson?.organizationIdentifier],
  );

  const { columns } = InvitationListTableColumns({
    resendInvite,
    revokeInvite,
    editRow,
    saveEditRow,
    cancelEditRow,
    loadingRow,
    editingRowId,
    editEmail,
    editEmailError,
    handleEditEmailChange,
  });

  const fetchWorkforceListData = useCallback(async () => {
    setTableLoading(true);
    const payload = {
      page: 1,
      organizationIdentifier: userJson?.organizationIdentifier,
      filters: {
        jobTitle: workforceRole.map((role) => role?.value),
        status: workforceStatus.map((status) => status?.value),
      },
    };
    const responseData = await executeIntent(
      IntentType.GET_ALL_WORKFORCE,
      payload,
    );
    setTableData(Array.isArray(responseData?.workforceList) ? responseData?.workforceList : []);
    setTableLoading(false);
    if (responseData?.workforceList?.length < 100) {
      setHasMoreData(false);
    } else {
      setPage(2);
      setHasMoreData(true);
    }
  }, [
    workforceRole,
    workforceStatus,
    userJson?.organizationIdentifier,
    setTableData,
    setTableLoading,
    setHasMoreData,
    setPage,
  ]);

  const fetchMoreData = useCallback(async () => {
    const payload = {
      page: page,
      organizationIdentifier: userJson?.organizationIdentifier,
      filters: {
        jobTitle: workforceRole.map((role) => role?.value),
        status: workforceStatus.map((status) => status?.value),
      },
    };
    if (hasMoreData) {
      const responseData = await executeIntent(
        IntentType.GET_ALL_WORKFORCE,
        payload,
      );
      if (responseData?.workforceList?.length > 0) {
        setTableData((prevData) => [...prevData, ...responseData?.workforceList]);
        setPage(page + 1);
      } else {
        setHasMoreData(false);
      }
    }
  }, [
    page,
    hasMoreData,
    userJson?.organizationIdentifier,
    workforceRole,
    workforceStatus,
    setTableData,
    setPage,
    setHasMoreData,
  ]);

  const fetchSearchedData = useCallback(async () => {
    setTableLoading(true);
    const searchPayload = {
      page: 1,
      query: searchQuery,
      organizationIdentifier: userJson?.organizationIdentifier,
      filters: {
        jobTitle: workforceRole.map((role) => role?.value),
        status: workforceStatus.map((status) => status?.value),
      },
    };

    const responseData = await executeIntent(
      IntentType.SEARCH_WORKFORCE,
      searchPayload,
    );
    setTableData(Array.isArray(responseData?.workforceList) ? responseData?.workforceList : []);
    setTableLoading(false);

    if (responseData?.workforceList?.length < 100) {
      setHasMoreData(false);
    } else {
      setPage(2);
      setHasMoreData(true);
    }
  }, [
    searchQuery,
    workforceRole,
    workforceStatus,
    userJson?.organizationIdentifier,
    setTableData,
    setTableLoading,
    setHasMoreData,
    setPage,
  ]);

  const handleRefreshInviteList = useCallback(() => {
    fetchWorkforceListData();
  }, [fetchWorkforceListData]);

  useEffect(() => {
    // access control for add volunteer button
    const configurationShowAddVolunteerButton =
      configuration[
        ConfigurationConstants.FEATUREACCESS_WORKFORCE_ADD_WORKFORCE
      ];
    setShowAddVolunteerButton(configurationShowAddVolunteerButton?.visibility);
  }, [configuration]);

  useEffect(() => {
    if (searchQuery.length > 0) {
      fetchSearchedData();
    } else if (searchQuery.length === 0) {
      fetchWorkforceListData();
    }
  }, [
    searchQuery,
    teams,
    reload,
    workforceRole,
    workforceStatus,
    fetchSearchedData,
    fetchWorkforceListData,
  ]);

  return (
    <div className="manage-invitation-page">
      <div className="form-renderer-back-button">
        <BackButton />
        <p>Manage Team Invitations</p>
      </div>

      <div className="careplan-wrapper row" style={{ marginBlock: 10 }}>
        <div className="col-12 col-md-4 careplan-search-wrapper">
          <SearchInput
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            cssClass="searchFilterContainer"
            searchPlaceholder={"Search Team Member..."}
          />
        </div>

        <div className="col-12 col-md-4">
          <div className="careplan-select-wrapper">
            <SearchAndSelect
              mode="multiple"
              selectedValues={workforceStatus}
              placeholder="Click here to select status"
              setValue={setWorkforceStatus}
              style={{ width: "100%" }}
              options={STATUS_FILTER_OPTIONS}
              selectOptionType="Simple"
              noDebounceSearch={true}
            />
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="careplan-select-wrapper">
            <SearchAndSelect
              mode="multiple"
              selectedValues={workforceRole}
              placeholder="Click here to select role"
              setValue={setWorkforceRole}
              style={{
                width: "100%",
              }}
              options={ROLE_FILTER_OPTIONS}
              selectOptionType="Simple"
              noDebounceSearch={true}
            />
          </div>
        </div>

        <Row style={{ gap: 4 }} justify="end">
          <Col>
            {showAddVolunteerButton && (
              <PrimaryButton
                buttonText="Invite Team Member"
                handleOnClick={handleAddWorkforceClick}
              />
            )}
          </Col>
          <Col>
            <PrimaryButton
              buttonText="Refresh"
              handleOnClick={handleRefreshInviteList}
            />
          </Col>
        </Row>
      </div>

      <div className="manage-invitation-table">
        <TableList
          data={tableData}
          columns={columns}
          hasMoreData={hasMoreData}
          searchQuery={searchQuery}
          tableLoading={tableLoading}
          fetchMoreData={fetchMoreData}
        />
      </div>

      {openAddWorkforceModal && (
        <AddEditWorkforceModal
          openAddWorkforceModal={openAddWorkforceModal}
          setOpenAddWorkforceModal={setOpenAddWorkforceModal}
          showAdditionalFields={false}
        />
      )}
    </div>
  );
};

export default ManageInvitationComponent;
